// src/utilies/laneConnectorTools/Add.js
import * as Cesium from "cesium";
import Type from "../Types";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

const timeNow = () => Cesium.JulianDate.now();

const DEFAULTS = {
  searchRadiusMeters: 40,
  candidateDistanceToleranceMeters: 1,
  maxGapAngleDeg: 7,
  angle: 7,
  maxConnectedAngleDeg: 10,
  connectedSnapMeters: 0.005,

  zoomDuration: 0.45,
  zoomRangeMultiplier: 6,
  zoomMinRangeMeters: 80,
  zoomMaxRangeMeters: 2000,
};

const normKey = (s) => (typeof s === "string" ? s.trim().toLowerCase() : "");
const deg = (r) => (r * 180) / Math.PI;

const DEBUG = false;
const d0 = (x) => (Number.isFinite(x) ? String(Math.round(x)) : String(x));

// --- fast ground distance (no height) ---
const _geodesic = new Cesium.EllipsoidGeodesic();
const _cartoScratchA = new Cesium.Cartographic();
const _cartoScratchB = new Cesium.Cartographic();

const toCartographicScratch = (c, scratch) => {
  try {
    if (!c) return null;
    const res = Cesium.Cartographic.fromCartesian(c, Cesium.Ellipsoid.WGS84, scratch);
    if (!res) return null;
    return res;
  } catch {
    return null;
  }
};

const groundDistanceCarto = (ca, cb) => {
  if (!ca || !cb) return Number.POSITIVE_INFINITY;
  try {
    _geodesic.setEndPoints(ca, cb);
    const d = _geodesic.surfaceDistance;
    return Number.isFinite(d) ? d : Number.POSITIVE_INFINITY;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
};

const groundDistanceMeters = (a, b) => {
  const ca = toCartographicScratch(a, _cartoScratchA);
  const cb = toCartographicScratch(b, _cartoScratchB);
  return groundDistanceCarto(ca, cb);
};

const clampRect = (a, b) => {
  const x1 = Math.min(a.x, b.x);
  const y1 = Math.min(a.y, b.y);
  const x2 = Math.max(a.x, b.x);
  const y2 = Math.max(a.y, b.y);
  return { x1, y1, x2, y2, width: x2 - x1, height: y2 - y1 };
};

const _vScratchA = new Cesium.Cartesian3();
const _vScratchB = new Cesium.Cartesian3();
const _vScratchC = new Cesium.Cartesian3();

const _bsScratch = new Cesium.BoundingSphere();
const _bsScratch2 = new Cesium.BoundingSphere();

const normalizeTo = (v, out) => {
  const m = Cesium.Cartesian3.magnitude(v);
  if (!Number.isFinite(m) || m < 1e-9) return null;
  return Cesium.Cartesian3.divideByScalar(v, m, out);
};

const angleDegBetweenUnit = (u, v) => {
  const dot = Cesium.Math.clamp(Cesium.Cartesian3.dot(u, v), -1, 1);
  return deg(Math.acos(dot));
};

const _mScratch = new Cesium.Matrix4();
const _mInvScratch = new Cesium.Matrix4();
const _enuA = new Cesium.Cartesian3();
const _enuB = new Cesium.Cartesian3();

const toEnuNoUpUnit = (origin, dirEcef, out) => {
  try {
    if (!origin || !dirEcef) return null;

    Cesium.Transforms.eastNorthUpToFixedFrame(origin, Cesium.Ellipsoid.WGS84, _mScratch);
    Cesium.Matrix4.inverseTransformation(_mScratch, _mInvScratch);

    // transform direction vector (ECEF) to local ENU
    Cesium.Matrix4.multiplyByPointAsVector(_mInvScratch, dirEcef, out);

    // remove Up
    out.z = 0;

    const m = Math.hypot(out.x, out.y);
    if (!Number.isFinite(m) || m < 1e-9) return null;

    out.x /= m;
    out.y /= m;
    out.z = 0;
    return out;
  } catch {
    return null;
  }
};

const angleDegBetweenGround = (origin, dirA, dirB) => {
  const a = toEnuNoUpUnit(origin, dirA, _enuA);
  const b = toEnuNoUpUnit(origin, dirB, _enuB);
  if (!a || !b) return 9999;

  const dot2 = Cesium.Math.clamp(a.x * b.x + a.y * b.y, -1, 1);
  return deg(Math.acos(dot2));
};

const angleDegBetween = (v1, v2) => {
  const u = normalizeTo(v1, _vScratchB);
  const v = normalizeTo(v2, _vScratchC);
  if (!u || !v) return 9999;
  return angleDegBetweenUnit(u, v);
};

const getEntityPositions = (entity, t) => {
  try {
    const p = entity?.polyline?.positions;
    if (!p) return null;
    const arr = typeof p.getValue === "function" ? p.getValue(t) : p;
    if (!Array.isArray(arr) || arr.length < 2) return null;
    return arr;
  } catch {
    return null;
  }
};

const getLineEndpoints = (entity, t) => {
  const pos = getEntityPositions(entity, t);
  if (!pos || pos.length < 2) return null;

  const start = pos[0];
  const end = pos[pos.length - 1];
  if (!start || !end) return null;

  const d = groundDistanceMeters(start, end);
  if (!Number.isFinite(d) || d < 1e-6) return null;

  return { start, end, positions: pos };
};

const layerKeyFromEntity = (entity, t) => {
  const tt = t || timeNow();
  const raw = entity?.properties?.layerName?.getValue?.(tt) ?? entity?.properties?.layerName ?? entity?.properties?.layer_name?.getValue?.(tt) ?? entity?.properties?.layer_name ?? "";
  return normKey(String(raw));
};

const isAllowed = (layerKey) => {
  const k = normKey(layerKey);
  const isLaneMarker = k.includes("lane_marker") || k.includes("lanemarker") || k.includes("lane marker");
  const isVirtual = k.includes("virtual_line") || k.includes("virtualline") || k.includes("virtual line") || k.includes("virtual");
  return isLaneMarker || isVirtual;
};

const projectToWindow = (viewer, world) => {
  try {
    if (!world) return null;
    const c = viewer.scene.cartesianToCanvasCoordinates(world);
    if (!c) return null;
    return { x: c.x, y: c.y };
  } catch {
    return null;
  }
};

const anyPointInsideRect = (viewer, pts, rect) => {
  for (let i = 0; i < pts.length; i++) {
    const w = projectToWindow(viewer, pts[i]);
    if (!w) continue;
    if (w.x >= rect.x1 && w.x <= rect.x2 && w.y >= rect.y1 && w.y <= rect.y2) return true;
  }
  return false;
};

const pickPolylineByClick = (viewer, position) => {
  try {
    const p1 = viewer.scene.pick(position);
    const ent1 = p1?.id || p1?.primitive?.id || null;
    if (ent1?.polyline) return ent1;

    const picks = viewer.scene.drillPick(position, 20) || [];
    for (const p of picks) {
      const ent = p?.id || p?.primitive?.id || null;
      if (!ent?.polyline) continue;
      return ent;
    }
    return null;
  } catch {
    return null;
  }
};

const scanPolylinesInRect = (viewer, rect, dataSources) => {
  const t = timeNow();
  const out = [];
  for (const ds of dataSources) {
    const ents = ds?.entities?.values || [];
    for (const e of ents) {
      if (!e?.polyline) continue;
      const ep = getLineEndpoints(e, t);
      if (!ep) continue;
      if (!anyPointInsideRect(viewer, ep.positions, rect)) continue;
      out.push(e);
    }
  }
  return out;
};

const extractNumericId = (raw, t) => {
  if (raw == null) return NaN;

  let v = raw;
  if (typeof v === "object") {
    try {
      v = v.getValue?.(t) ?? String(v);
    } catch {
      return NaN;
    }
  }

  if (typeof v === "number") return v;

  const s = String(v).trim();
  const m = s.match(/(\d+)\s*$/) || s.match(/(\d+)/);
  if (!m) return NaN;
  return Number(m[1]);
};

const getLastPolylineEntity = (ds) => {
  const ents = ds?.entities?.values || [];
  for (let i = ents.length - 1; i >= 0; i--) {
    if (ents[i]?.polyline) return ents[i];
  }
  return null;
};

const propertyBagToPlain = (bag) => {
  const t = timeNow();
  if (!bag) return {};
  try {
    const v = typeof bag.getValue === "function" ? bag.getValue(t) : null;
    if (v && typeof v === "object") return { ...v };
  } catch {}

  const out = {};
  const names = bag?.propertyNames || [];
  for (const k of names) {
    try {
      out[k] = bag[k]?.getValue?.(t) ?? bag[k];
    } catch {}
  }
  return out;
};

// --- spatial index (ECEF grid) ---
const cellKeyFromCartesian = (c, cellSize) => {
  const s = cellSize > 0 ? cellSize : 1;
  const ix = Math.floor(c.x / s);
  const iy = Math.floor(c.y / s);
  const iz = Math.floor(c.z / s);
  return `${ix}|${iy}|${iz}`;
};

// -----------------------
// State
// -----------------------
const makeInitialState = () => ({
  enabled: false,
  viewer: null,
  handler: null,

  laneMarkerDs: null,
  virtualLineDs: null,

  originalCameraControls: null,
  camLocked: false,

  onDisable: null,

  overlayEl: null,
  overlayRafId: 0,
  overlayPendingRect: null,

  dragging: false,
  dragStart: null,
  dragEnd: null,

  phase: "pickStart", // pickStart | pickCandidate | waitCommit
  startLine: null,

  candidates: [],
  chosenCandidate: null,

  autoAdvance: true,
  nextStartLine: null,

  cfg: { ...DEFAULTS },

  highlightCache: new Map(), // entityId -> { entity, material, width, group }
  currentHighlightId: null,
  markEdited: null, // (targetDsKey) => void
  userData: null,
  mapObjectsRef: null,
  setMapObjects: null,

  maxIdCache: new Map(), // ds -> lastIssuedIdOrMaxId
  lastStyleCache: new Map(), // ds -> { width, material, clampToGround }
  lastPropsCache: new Map(), // ds -> plainProps

  entityCache: new Map(), // entityId -> cache
  startIndex: new Map(), // cellKey -> entityId[]
  endIndex: new Map(), // cellKey -> entityId[]
  visitedIds: new Set(),

  cellSizeMeters: DEFAULTS.searchRadiusMeters,
});

let st = makeInitialState();

const layerNameFromDataSource = (entity, t) => {
  const fromProps = layerKeyFromEntity(entity, t);
  if (fromProps) return fromProps;

  const id = entity?.id;

  const inLaneMarker = typeof st.laneMarkerDs?.entities?.contains === "function" ? st.laneMarkerDs.entities.contains(entity) : st.laneMarkerDs?.entities?.getById?.(id) != null;

  if (inLaneMarker) return "lane_marker";

  const inVirtual = typeof st.virtualLineDs?.entities?.contains === "function" ? st.virtualLineDs.entities.contains(entity) : st.virtualLineDs?.entities?.getById?.(id) != null;

  if (inVirtual) return "virtual_line";

  return "";
};

const lockCamera = () => {
  if (!st.viewer || st.camLocked) return;
  disableCameraControls(st.viewer, st.originalCameraControls);
  st.camLocked = true;
};

const unlockCamera = () => {
  if (!st.viewer || !st.camLocked) return;
  restoreCameraControls(st.viewer, st.originalCameraControls);
  st.camLocked = false;
};

const ensureOverlay = () => {
  if (st.overlayEl) return;
  const canvas = st.viewer?.scene?.canvas;
  if (!canvas) return;

  const el = document.createElement("div");
  el.style.position = "absolute";
  el.style.pointerEvents = "none";
  el.style.border = "2px dashed #22c55e";
  el.style.background = "rgba(34,197,94,0.08)";
  el.style.display = "none";

  const parent = canvas.parentElement || document.body;
  parent.style.position = parent.style.position || "relative";
  parent.appendChild(el);
  st.overlayEl = el;
};

const applyOverlayRect = (rect) => {
  if (!st.overlayEl) return;

  if (!rect) {
    st.overlayEl.style.display = "none";
    return;
  }

  st.overlayEl.style.display = "block";
  st.overlayEl.style.left = `${rect.x1}px`;
  st.overlayEl.style.top = `${rect.y1}px`;
  st.overlayEl.style.width = `${rect.width}px`;
  st.overlayEl.style.height = `${rect.height}px`;
};

const drawOverlayRect = (rect) => {
  st.overlayPendingRect = rect;

  if (st.overlayRafId) return;

  st.overlayRafId = window.requestAnimationFrame(() => {
    st.overlayRafId = 0;
    applyOverlayRect(st.overlayPendingRect);
  });
};

const destroyOverlay = () => {
  try {
    if (st.overlayRafId) window.cancelAnimationFrame(st.overlayRafId);
  } catch {}
  st.overlayRafId = 0;

  try {
    if (st.overlayEl?.parentElement) st.overlayEl.parentElement.removeChild(st.overlayEl);
  } catch {}
  st.overlayEl = null;
};

const clearHighlights = (group = null) => {
  const arr = Array.from(st.highlightCache.values());
  for (const item of arr) {
    if (!item?.entity?.polyline) continue;
    if (group && item.group !== group) continue;

    try {
      item.entity.polyline.material = item.material;
      item.entity.polyline.width = item.width;
    } catch {}
    st.highlightCache.delete(item.entity.id);
  }

  if (!group || group === "current") st.currentHighlightId = null;
};

const saveHighlightIfNeeded = (entity, group) => {
  if (!entity?.id || !entity?.polyline) return;
  if (st.highlightCache.has(entity.id)) return;

  const t = timeNow();
  const material = entity.polyline.material;
  const width = entity.polyline.width?.getValue?.(t) ?? entity.polyline.width ?? 2;

  st.highlightCache.set(entity.id, { entity, material, width, group });
};

const highlightEntities = (entities) => {
  clearHighlights("candidate");
  const t = timeNow();
  for (const e of entities) {
    if (!e?.polyline) continue;
    saveHighlightIfNeeded(e, "candidate");
    e.polyline.material = Cesium.Color.RED;
    e.polyline.width = (e.polyline.width?.getValue?.(t) ?? e.polyline.width ?? 2) + 1;
  }
};

const highlightCurrentLine = (entity) => {
  if (!entity?.polyline || !entity?.id) return;

  if (st.currentHighlightId && st.currentHighlightId !== entity.id) {
    clearHighlights("current");
  }

  st.currentHighlightId = entity.id;

  saveHighlightIfNeeded(entity, "current");
  const t = timeNow();
  entity.polyline.material = Cesium.Color.GREEN;
  entity.polyline.width = (entity.polyline.width?.getValue?.(t) ?? entity.polyline.width ?? 2) + 2;

  // zoom is handled in tryAdvance to include candidates too
};

const zoomToEntities = (entities) => {
  const viewer = st.viewer;
  if (!viewer || !entities || entities.length === 0) return;

  const t = timeNow();
  const pts = [];

  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    if (!e?.polyline) continue;
    const pos = getEntityPositions(e, t);
    if (!pos || pos.length < 2) continue;
    for (let k = 0; k < pos.length; k++) pts.push(pos[k]);
  }

  if (pts.length < 2) return;

  try {
    Cesium.BoundingSphere.fromPoints(pts, _bsScratch);
    Cesium.BoundingSphere.clone(_bsScratch, _bsScratch2);

    const r = _bsScratch2.radius;
    const want = Cesium.Math.clamp(r * st.cfg.zoomRangeMultiplier, st.cfg.zoomMinRangeMeters, st.cfg.zoomMaxRangeMeters);

    viewer.camera.flyToBoundingSphere(_bsScratch2, {
      duration: st.cfg.zoomDuration,
      offset: new Cesium.HeadingPitchRange(viewer.camera.heading, viewer.camera.pitch, want),
    });
  } catch {}
};

const zoomToEntity = (entity) => {
  const viewer = st.viewer;
  if (!viewer || !entity?.polyline) return;

  const t = timeNow();
  const pos = getEntityPositions(entity, t);
  if (!pos || pos.length < 2) return;

  try {
    Cesium.BoundingSphere.fromPoints(pos, _bsScratch);
    Cesium.BoundingSphere.clone(_bsScratch, _bsScratch2);

    const r = _bsScratch2.radius;
    const want = Cesium.Math.clamp(r * st.cfg.zoomRangeMultiplier, st.cfg.zoomMinRangeMeters, st.cfg.zoomMaxRangeMeters);

    viewer.camera.flyToBoundingSphere(_bsScratch2, {
      duration: st.cfg.zoomDuration,
      offset: new Cesium.HeadingPitchRange(viewer.camera.heading, viewer.camera.pitch, want),
    });
  } catch {}
};

const cellRadiusSteps = (radiusMeters, cellSizeMeters) => {
  const s = Math.max(1e-6, cellSizeMeters);
  const r = Math.max(0, radiusMeters);
  return Math.max(1, Math.ceil(r / s));
};

const queryIndexNearPoint = (point, radiusMeters, indexMap) => {
  if (!point || !indexMap) return [];
  const steps = cellRadiusSteps(radiusMeters, st.cellSizeMeters);
  const baseKey = cellKeyFromCartesian(point, st.cellSizeMeters);
  const [ix0, iy0, iz0] = baseKey.split("|").map((x) => Number(x));

  const outIds = [];

  for (let dx = -steps; dx <= steps; dx++) {
    for (let dy = -steps; dy <= steps; dy++) {
      for (let dz = -steps; dz <= steps; dz++) {
        const key = `${ix0 + dx}|${iy0 + dy}|${iz0 + dz}`;
        const arr = indexMap?.get(key);
        if (!arr || arr.length === 0) continue;
        for (let i = 0; i < arr.length; i++) outIds.push(arr[i]);
      }
    }
  }
  return outIds;
};

const getOrUpdateEntityCache = (entity, t) => {
  if (!entity?.id || !entity?.polyline) return null;

  let cache = st.entityCache.get(entity.id);
  if (!cache) {
    cache = {
      id: entity.id,
      entity,
      valid: false,

      start: null,
      end: null,

      startCarto: null,
      endCarto: null,

      dirUnit: new Cesium.Cartesian3(),
      dirUnitNeg: new Cesium.Cartesian3(),

      firstSegUnit: new Cesium.Cartesian3(),
      firstSegUnitNeg: new Cesium.Cartesian3(),
      hasFirstSeg: false,

      lastSegUnit: new Cesium.Cartesian3(),
      lastSegUnitNeg: new Cesium.Cartesian3(),
      hasLastSeg: false,

      layerKey: "",

      cellKeyStart: "",
      cellKeyEnd: "",
    };
    st.entityCache.set(entity.id, cache);
  }

  const ep = getLineEndpoints(entity, t);
  if (!ep) {
    cache.valid = false;
    return cache;
  }

  if (cache.start !== ep.start || cache.end !== ep.end) {
    cache.start = ep.start;
    cache.end = ep.end;
    cache.startCarto = toCartographicScratch(ep.start, new Cesium.Cartographic());
    cache.endCarto = toCartographicScratch(ep.end, new Cesium.Cartographic());

    // Whole-line direction (start -> end)
    Cesium.Cartesian3.subtract(cache.end, cache.start, _vScratchA);
    const uLine = normalizeTo(_vScratchA, cache.dirUnit);
    if (uLine) Cesium.Cartesian3.negate(cache.dirUnit, cache.dirUnitNeg);

    // First segment direction (p1 -> p2)
    cache.hasFirstSeg = false;
    cache.hasLastSeg = false;

    if (ep.positions.length >= 2) {
      Cesium.Cartesian3.subtract(ep.positions[1], ep.positions[0], _vScratchA);
      const uFirst = normalizeTo(_vScratchA, cache.firstSegUnit);
      cache.hasFirstSeg = Boolean(uFirst);
      if (uFirst) Cesium.Cartesian3.negate(cache.firstSegUnit, cache.firstSegUnitNeg);
    }

    if (ep.positions.length >= 2) {
      const n = ep.positions.length;
      Cesium.Cartesian3.subtract(ep.positions[n - 1], ep.positions[n - 2], _vScratchA);
      const uLast = normalizeTo(_vScratchA, cache.lastSegUnit);
      cache.hasLastSeg = Boolean(uLast);
      if (uLast) Cesium.Cartesian3.negate(cache.lastSegUnit, cache.lastSegUnitNeg);
    }

    cache.valid = Boolean(uLine);

    cache.cellKeyStart = cache.valid ? cellKeyFromCartesian(cache.start, st.cellSizeMeters) : "";
    cache.cellKeyEnd = cache.valid ? cellKeyFromCartesian(cache.end, st.cellSizeMeters) : "";
  }

  cache.layerKey = layerNameFromDataSource(entity, t);
  return cache;
};

const rebuildIndexAndCaches = () => {
  st.entityCache.clear();
  st.startIndex.clear();
  st.endIndex.clear();
  st.lastStyleCache.clear();
  st.lastPropsCache.clear();
  st.maxIdCache.clear();

  st.cellSizeMeters = Math.max(1, st.cfg.searchRadiusMeters);

  const t = timeNow();

  const dsList = [
    { ds: st.laneMarkerDs, key: "lanemarker" },
    { ds: st.virtualLineDs, key: "virtualline" },
  ];

  for (const item of dsList) {
    const ds = item.ds;
    if (!ds) continue;

    let mx = 0;

    const ents = ds?.entities?.values || [];
    for (const e of ents) {
      if (!e?.polyline) continue;

      const key = layerNameFromDataSource(e, t);
      if (!isAllowed(key)) continue;

      const cache = getOrUpdateEntityCache(e, t);
      if (!cache?.valid) continue;

      const ckS = cache.cellKeyStart;
      if (ckS) {
        let arrS = st.startIndex.get(ckS);
        if (!arrS) {
          arrS = [];
          st.startIndex.set(ckS, arrS);
        }
        arrS.push(cache.id);
      }

      const ckE = cache.cellKeyEnd;
      if (ckE) {
        let arrE = st.endIndex.get(ckE);
        if (!arrE) {
          arrE = [];
          st.endIndex.set(ckE, arrE);
        }
        arrE.push(cache.id);
      }

      const rawId = e?.properties?.id;
      const nid = extractNumericId(rawId, t);
      if (Number.isFinite(nid) && nid > mx) mx = nid;
    }

    st.maxIdCache.set(ds, mx);
  }
};

const pickStyleFromCache = (ds) => {
  const cached = st.lastStyleCache.get(ds);
  if (cached) return cached;
  const ent = getLastPolylineEntity(ds) || ds?.entities?.values?.find((e) => e?.polyline);
  if (!ent?.polyline) return null;

  const t = timeNow();
  const style = {
    width: ent.polyline.width?.getValue?.(t) ?? ent.polyline.width ?? 2,
    material: ent.polyline.material ?? Cesium.Color.WHITE,
    clampToGround: ent.polyline.clampToGround ?? true,
  };
  st.lastStyleCache.set(ds, style);
  return style;
};

const pickPropsFromCache = (ds) => {
  const cached = st.lastPropsCache.get(ds);
  if (cached) return cached;

  const last = getLastPolylineEntity(ds);
  if (!last?.properties) return {};
  const props = propertyBagToPlain(last.properties);
  st.lastPropsCache.set(ds, props);
  return props;
};

const getDirectionalCache = (entity, t) => {
  const c = getOrUpdateEntityCache(entity, t);
  if (!c?.valid) return null;

  const firstU = c.hasFirstSeg ? c.firstSegUnit : c.dirUnit;
  const lastU = c.hasLastSeg ? c.lastSegUnit : c.dirUnit;

  return {
    base: c,
    dStart: c.start,
    dEnd: c.end,
    dStartCarto: c.startCarto,
    dEndCarto: c.endCarto,
    dDirAtStartUnit: firstU,
    dDirAtEndUnit: lastU,
  };
};

const findBestConnectedNext = (baseLine) => {
  const t = timeNow();

  const baseD = getDirectionalCache(baseLine, t);
  if (!baseD) return null;

  // Only end -> start direct connection (distance-only)
  const idsS = queryIndexNearPoint(baseD.dEnd, st.cfg.connectedSnapMeters, st.startIndex);

  const seen = new Set();
  let best = null; // { entity, dist }

  for (let i = 0; i < idsS.length; i++) {
    const id = idsS[i];
    if (seen.has(id)) continue;
    seen.add(id);

    const e = st.entityCache.get(id)?.entity;
    if (!e?.polyline) continue;
    if (e === baseLine) continue;
    if (st.visitedIds.has(e.id)) continue;

    const c = getOrUpdateEntityCache(e, t);
    if (!c?.valid) continue;

    const key = c.layerKey;
    if (!isAllowed(key)) continue;

    const chord = Cesium.Cartesian3.distance(baseD.dEnd, c.start);
    if (chord > st.cfg.connectedSnapMeters) continue;

    const d = groundDistanceCarto(baseD.dEndCarto, c.startCarto);
    if (d > st.cfg.connectedSnapMeters) continue;

    if (!best || d < best.dist) best = { entity: e, dist: d };
  }

  return best?.entity || null;
};

const findReverseConnectedAtEnd = (baseLine) => {
  const t = timeNow();

  const baseD = getDirectionalCache(baseLine, t);
  if (!baseD) return null;

  // Reverse connection: current end -> other end (distance-only)
  const idsE = queryIndexNearPoint(baseD.dEnd, st.cfg.connectedSnapMeters, st.endIndex);

  const seen = new Set();
  for (let i = 0; i < idsE.length; i++) {
    const id = idsE[i];
    if (seen.has(id)) continue;
    seen.add(id);

    const e = st.entityCache.get(id)?.entity;
    if (!e?.polyline) continue;
    if (e === baseLine) continue;

    const c = getOrUpdateEntityCache(e, t);
    if (!c?.valid) continue;

    const key = c.layerKey;
    if (!isAllowed(key)) continue;

    const chord = Cesium.Cartesian3.distance(baseD.dEnd, c.end);
    if (chord > st.cfg.connectedSnapMeters) continue;

    const d = groundDistanceCarto(baseD.dEndCarto, c.endCarto);
    if (d > st.cfg.connectedSnapMeters) continue;

    return e; // reverse-connected line found
  }

  return null;
};

const computeCandidatesNear = (baseLine) => {
  const t = timeNow();

  const baseD = getDirectionalCache(baseLine, t);
  if (!baseD) return { candidates: [], ambiguous: false, diag: "Start line geometry is invalid." };

  const baseEnd = baseD.dEnd;
  const baseEndCarto = baseD.dEndCarto;
  const baseDirUnit = baseD.dDirAtEndUnit;

  const radius = st.cfg.searchRadiusMeters;
  const angleLimit = st.cfg.maxGapAngleDeg;

  const stats = {
    withinRadius: 0,
    minAngle: Number.POSITIVE_INFINITY,
  };

  const idsS = queryIndexNearPoint(baseEnd, radius, st.startIndex);

  const seen = new Set();
  const out = [];

  for (let i = 0; i < idsS.length; i++) {
    const id = idsS[i];
    if (seen.has(id)) continue;
    seen.add(id);

    const e = st.entityCache.get(id)?.entity;
    if (!e?.polyline) continue;
    if (e === baseLine) continue;
    if (st.visitedIds.has(e.id)) continue;

    const cache = getOrUpdateEntityCache(e, t);
    if (!cache?.valid) continue;

    const key = cache.layerKey;
    if (!isAllowed(key)) continue;

    const chord = Cesium.Cartesian3.distance(baseEnd, cache.start);
    if (chord > radius) continue;

    const d = groundDistanceCarto(baseEndCarto, cache.startCarto);
    if (d > radius) continue;

    stats.withinRadius++;

    // Connector direction from current end to candidate start
    Cesium.Cartesian3.subtract(cache.start, baseEnd, _vScratchA);

    const a = angleDegBetweenGround(baseEnd, baseDirUnit, _vScratchA);
    if (a > angleLimit) continue;

    if (a < stats.minAngle) stats.minAngle = a;

    const aheadDot = Cesium.Cartesian3.dot(baseDirUnit, _vScratchA);
    if (d > 0.5 && aheadDot < 0) continue;

    out.push({ entity: e, dist: d, angle: a });
  }

  if (out.length === 0) {
    // Fallback scan (when index misses)
    const sources = [st.laneMarkerDs, st.virtualLineDs].filter(Boolean);
    for (const ds of sources) {
      const ents = ds.entities.values || [];
      for (const e of ents) {
        if (!e?.polyline) continue;
        if (e === baseLine) continue;
        if (st.visitedIds.has(e.id)) continue;

        const key = layerNameFromDataSource(e, t);
        if (!isAllowed(key)) continue;

        const cache = getOrUpdateEntityCache(e, t);
        if (!cache?.valid) continue;

        const d = groundDistanceCarto(baseEndCarto, cache.startCarto);
        if (d > radius) continue;

        stats.withinRadius++;

        Cesium.Cartesian3.subtract(cache.start, baseEnd, _vScratchA);

        const a = angleDegBetweenGround(baseEnd, baseDirUnit, _vScratchA);
        if (a > angleLimit) continue;

        if (a < stats.minAngle) stats.minAngle = a;

        const aheadDot = Cesium.Cartesian3.dot(baseDirUnit, _vScratchA);
        if (d > 0.5 && aheadDot < 0) continue;

        out.push({ entity: e, dist: d, angle: a });
      }
    }
  }

  if (out.length === 0) {
    if (stats.withinRadius === 0) {
      return {
        candidates: [],
        ambiguous: false,
        diag: "No candidate start point found within radius " + d0(radius) + " m.",
      };
    }

    return {
      candidates: [],
      ambiguous: false,
      diag:
        "Found " + stats.withinRadius + " start point(s) within radius " + d0(radius) + " m, but none aligned (angle limit " + d0(angleLimit) + "°). Closest angle was " + d0(stats.minAngle) + "°.",
    };
  }

  const pruned = pruneCandidateOutliers(out);

  pruned.sort((a, b) => a.dist - b.dist || a.angle - b.angle);

  return {
    candidates: pruned.map((x) => x.entity),
    ambiguous: pruned.length > 1,
    diag: null,
  };
};

const medianOfSorted = (arr) => {
  const n = arr.length;
  if (n === 0) return NaN;
  const mid = Math.floor(n / 2);
  if (n % 2 === 1) return arr[mid];
  return (arr[mid - 1] + arr[mid]) / 2;
};

const keepFirstClusterByGaps = (sorted, getVal, absCut, multCut) => {
  if (!sorted || sorted.length <= 1) return sorted || [];

  const gaps = [];
  for (let i = 1; i < sorted.length; i++) {
    const a = getVal(sorted[i - 1]);
    const b = getVal(sorted[i]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue;

    const gap = b - a;
    if (!Number.isFinite(gap) || gap < 0) continue;

    if (gaps.length >= 1) {
      const gs = gaps.slice().sort((x, y) => x - y);
      const med = medianOfSorted(gs);
      const thr = Math.max(absCut, Number.isFinite(med) ? med * multCut : absCut);
      if (gap > thr) return sorted.slice(0, i);
    }

    gaps.push(gap);
  }

  return sorted;
};

const pruneCandidateOutliers = (arr) => {
  if (!arr || arr.length <= 1) return arr || [];

  const distAbs = Number.isFinite(st.cfg?.candidateOutlierDistGapMeters) ? st.cfg.candidateOutlierDistGapMeters : 8;
  const angleAbs = Number.isFinite(st.cfg?.candidateOutlierAngleGapDeg) ? st.cfg.candidateOutlierAngleGapDeg : 2;
  const mult = Number.isFinite(st.cfg?.candidateOutlierGapMultiplier) ? st.cfg.candidateOutlierGapMultiplier : 3;

  // priority 1: distance
  let a = arr.slice().sort((x, y) => x.dist - y.dist || x.angle - y.angle);
  a = keepFirstClusterByGaps(a, (v) => v.dist, distAbs, mult);

  // priority 2: angle (only within kept distance cluster)
  let b = a.slice().sort((x, y) => x.angle - y.angle || x.dist - y.dist);
  b = keepFirstClusterByGaps(b, (v) => v.angle, angleAbs, mult);

  // final order: distance then angle
  b.sort((x, y) => x.dist - y.dist || x.angle - y.angle);
  return b;
};

const applyDistanceTolerance = (arr) => {
  if (!arr || arr.length === 0) return { entities: [], ambiguous: false };

  // arr: [{ entity, dist, angle }]
  arr.sort((a, b) => a.angle - b.angle || a.dist - b.dist);

  const bestDist = arr[0].dist;
  const tol = Number.isFinite(st.cfg?.candidateDistanceToleranceMeters) ? st.cfg.candidateDistanceToleranceMeters : 1;

  const near = [];
  for (let i = 0; i < arr.length; i++) {
    if (Math.abs(arr[i].dist - bestDist) <= tol) near.push(arr[i]);
  }

  if (near.length >= 2) return { entities: near.map((x) => x.entity), ambiguous: true };

  return { entities: [arr[0].entity], ambiguous: false };
};

const collectIdsFromGeoJSON = (gjson, out = new Set()) => {
  if (!gjson?.features || !Array.isArray(gjson.features)) return out;

  for (const f of gjson.features) {
    const n = Number(f?.properties?.id);
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
};

const collectIdsFromDataSource = (ds, out = new Set()) => {
  const t = timeNow();
  const ents = ds?.entities?.values || [];

  for (const e of ents) {
    const n = extractNumericId(e?.properties?.id, t);
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
};

const normalizeLayerIdentity = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s\-_]/g, "");

const findMapObjectByDataSource = (ds) => {
  const list = Array.isArray(st.mapObjectsRef?.current) ? st.mapObjectsRef.current : [];

  return (
    list.find((o) => {
      if (!o || !ds) return false;
      if (o.dataSource === ds || o.laneletDataSource === ds) return true;
      if (ds.layerId != null && o.layer_id != null && String(o.layer_id) === String(ds.layerId)) return true;

      const a = normalizeLayerIdentity(o.file_name || o.layer_name || o.name);
      const b = normalizeLayerIdentity(ds.layerName || ds.name);
      return a && b && a === b;
    }) || null
  );
};

const getStoredMaxIdFromLayer = (layer) => {
  const candidates = [layer?.bboxMaxFeaturePropertiesId, layer?.bboxMaxId];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }

  return null;
};

const buildUsedFeatureIdSet = (layer, ds) => {
  const ids = new Set();

  for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson, layer?.lenelet_geojson]) {
    collectIdsFromGeoJSON(gjson, ids);
  }

  collectIdsFromDataSource(ds, ids);

  return ids;
};

const getFallbackMaxIdForLayer = (layer, ds) => {
  let max = -Infinity;

  const cached = st.maxIdCache.get(ds);
  if (Number.isFinite(cached)) max = Math.max(max, cached);

  const lastId = Number(layer?.geojson_last_id);
  if (Number.isFinite(lastId)) max = Math.max(max, lastId);

  for (const id of buildUsedFeatureIdSet(layer, ds)) {
    if (id > max) max = id;
  }

  return Number.isFinite(max) ? max : 0;
};

const commitAllocatedIdToLayer = (layer, ds, nextId) => {
  if (!Number.isFinite(nextId)) return;

  st.maxIdCache.set(ds, nextId);

  if (layer) {
    layer.geojson_last_id = nextId;
    layer.bboxMaxId = nextId;
    layer.bboxMaxFeaturePropertiesId = nextId;
  }

  const patch = (o) => {
    if (!o || !ds) return o;

    const sameLayer =
      o === layer ||
      o.dataSource === ds ||
      o.laneletDataSource === ds ||
      (ds.layerId != null && o.layer_id != null && String(o.layer_id) === String(ds.layerId)) ||
      (normalizeLayerIdentity(o.file_name || o.layer_name || o.name) && normalizeLayerIdentity(o.file_name || o.layer_name || o.name) === normalizeLayerIdentity(ds.layerName || ds.name));

    if (!sameLayer) return o;

    return {
      ...o,
      geojson_last_id: nextId,
      bboxMaxId: nextId,
      bboxMaxFeaturePropertiesId: nextId,
    };
  };

  if (Array.isArray(st.mapObjectsRef?.current)) {
    st.mapObjectsRef.current = st.mapObjectsRef.current.map(patch);
  }

  if (typeof st.setMapObjects === "function") {
    st.setMapObjects((prev) => (Array.isArray(prev) ? prev.map(patch) : prev));
  }
};

const getNextIdFast = (ds) => {
  if (!ds) return 2;

  const layer = findMapObjectByDataSource(ds);
  const storedMax = getStoredMaxIdFromLayer(layer);
  const base = Number.isFinite(storedMax) ? storedMax : getFallbackMaxIdForLayer(layer, ds);
  const usedIds = buildUsedFeatureIdSet(layer, ds);

  let nextId = base + 2;
  while (usedIds.has(nextId)) {
    nextId += 2;
  }

  commitAllocatedIdToLayer(layer, ds, nextId);
  return nextId;
};

const indexNewEntity = (entity) => {
  if (!entity?.polyline || !entity?.id) return;

  const t = timeNow();
  const key = layerNameFromDataSource(entity, t);
  if (!isAllowed(key)) return;

  const cache = getOrUpdateEntityCache(entity, t);
  if (!cache?.valid) return;

  const ckS = cache.cellKeyStart;
  if (ckS) {
    let arrS = st.startIndex.get(ckS);
    if (!arrS) {
      arrS = [];
      st.startIndex.set(ckS, arrS);
    }
    arrS.push(cache.id);
  }

  const ckE = cache.cellKeyEnd;
  if (ckE) {
    let arrE = st.endIndex.get(ckE);
    if (!arrE) {
      arrE = [];
      st.endIndex.set(ckE, arrE);
    }
    arrE.push(cache.id);
  }
};

const createConnectorEntity = (fromLine, toLine, targetDs, targetDsKey) => {
  const t = timeNow();

  const aD = getDirectionalCache(fromLine, t);
  const bD = getDirectionalCache(toLine, t);
  if (!aD || !bD) return null;

  const style = pickStyleFromCache(targetDs) || { width: 2, material: Cesium.Color.WHITE, clampToGround: true };

  const propsBase = pickPropsFromCache(targetDs);
  const nextId = getNextIdFast(targetDs);
  const propsForNew = { ...propsBase, id: nextId };

  const creator = getUsernameAndUserIdFormat(st.userData);
  if (creator) propsForNew.creator = creator;

  const entity = new Cesium.Entity({
    polyline: {
      positions: [aD.dEnd, bD.dStart],
      width: style.width,
      material: style.material,
      clampToGround: style.clampToGround,
    },
    properties: new Cesium.PropertyBag(propsForNew),
  });

  targetDs.entities.add(entity);

  st.lastStyleCache.set(targetDs, style);
  st.lastPropsCache.set(targetDs, propsForNew);
  indexNewEntity(entity);

  try {
    if (typeof st.markEdited === "function") st.markEdited(targetDsKey);
  } catch {}

  return entity;
};

const tryAdvance = () => {
  while (true) {
    const cur = st.startLine;
    const curId = cur?.id;
    if (!curId) break;

    st.visitedIds.add(curId);

    const nxt = findBestConnectedNext(cur);
    if (DEBUG) {
      console.log("[LC] autoNext", cur?.id, "->", nxt?.id || null);
    }

    // If no correct (end->start) connection, only then check for reverse-only connection
    if (!nxt) {
      const rev = findReverseConnectedAtEnd(cur);
      if (rev) {
        showToast(`Reverse-only connection detected (end→end) with line "${rev.id}". Process stopped.`, Type.NOTIFICATION.ERROR, { duration: 6000 });

        st.phase = "pickStart";
        st.candidates = [];
        st.chosenCandidate = null;

        // Keep current (green) highlight
        highlightCurrentLine(st.startLine);

        return;
      }

      break;
    }

    const nxtId = nxt?.id;
    if (nxtId && st.visitedIds.has(nxtId)) {
      // loop detected -> stop auto-advance here
      break;
    }

    st.startLine = nxt;
  }

  highlightCurrentLine(st.startLine);

  const res = computeCandidatesNear(st.startLine);
  const cands = res.candidates;
  st.candidates = cands;

  st.chosenCandidate = null;

  // zoom to include current (green) + candidates (red)
  try {
    const pack = [st.startLine, ...cands].filter(Boolean).slice(0, 25);
    zoomToEntities(pack);
  } catch {}

  if (cands.length === 0) {
    showToast(res?.diag || "No candidate found. Process stopped.", Type.NOTIFICATION.WARNING, { duration: 4500 });

    st.phase = "pickStart";
    st.candidates = [];
    st.chosenCandidate = null;

    // keep st.startLine and keep current (green) highlight
    // DO NOT clearHighlights() here
    return;
  }

  if (cands.length > 1) {
    st.phase = "pickCandidate";
    highlightEntities(cands);
    showToast("Multiple candidates found. Click one. ESC to exit.", Type.NOTIFICATION.INFO, { duration: 5000 });
    return;
  }

  st.phase = "waitCommit";
  st.chosenCandidate = cands[0];
  highlightEntities(cands); // make the single candidate red too
  showToast("Candidate found. Press V (Virtual) or L (LaneMarker).", Type.NOTIFICATION.INFO, { duration: 5000 });
};

const acceptStartLine = (line) => {
  const t = timeNow();
  const key = layerNameFromDataSource(line, t);
  if (!isAllowed(key)) {
    showToast("Invalid selection. Only LaneMarker/VirtualLine allowed.", Type.NOTIFICATION.ERROR, { duration: 3500 });
    return;
  }

  if (st.autoAdvance) st.visitedIds.clear();

  st.startLine = line;
  st.nextStartLine = null;
  st.phase = "pickStart";
  showToast("Start selected. Searching forward...", Type.NOTIFICATION.SUCCESS, { duration: 2000 });
  tryAdvance();
};

const acceptCandidateClick = (line) => {
  if (!line || !st.candidates.includes(line)) return;

  st.chosenCandidate = line;
  st.phase = "waitCommit";

  // Clear other candidate highlights, but keep the chosen one red
  clearHighlights("candidate");

  const t = timeNow();
  if (line?.polyline) {
    saveHighlightIfNeeded(line, "candidate");
    line.polyline.material = Cesium.Color.RED;
    line.polyline.width = (line.polyline.width?.getValue?.(t) ?? line.polyline.width ?? 2) + 1;
  }

  showToast("Selected. Press V (Virtual) or L (LaneMarker).", Type.NOTIFICATION.INFO, { duration: 5000 });
};

const commitConnector = (targetKey) => {
  if (!st.startLine || !st.chosenCandidate) return;

  clearHighlights("candidate");

  const k = normKey(targetKey);

  if (k === "l") {
    if (!st.laneMarkerDs) {
      showToast("LaneMarker dataSource not found.", Type.NOTIFICATION.ERROR, { duration: 3500 });
      return;
    }
    createConnectorEntity(st.startLine, st.chosenCandidate, st.laneMarkerDs, "lanemarker");
  } else if (k === "v") {
    if (!st.virtualLineDs) {
      showToast("VirtualLine dataSource not found.", Type.NOTIFICATION.ERROR, { duration: 3500 });
      return;
    }
    createConnectorEntity(st.startLine, st.chosenCandidate, st.virtualLineDs, "virtualline");
  } else {
    return;
  }

  st.nextStartLine = st.chosenCandidate;

  st.chosenCandidate = null;
  st.phase = "pickStart";

  if (st.autoAdvance) {
    st.startLine = st.nextStartLine;
    if (st.startLine?.id) st.visitedIds.add(st.startLine.id);
    st.nextStartLine = null;

    showToast("Connector created. Continuing...", Type.NOTIFICATION.SUCCESS, { duration: 1800 });
    tryAdvance();
  } else {
    // stop after one step; next click should use nextStartLine
    showToast("Connector created. Click again to continue.", Type.NOTIFICATION.SUCCESS, { duration: 2200 });
    highlightCurrentLine(st.startLine); // current stays green
  }
};

const onKeyDown = (e) => {
  if (!st.enabled) return;

  if (e.key === "Escape") {
    e.preventDefault();
    try {
      st.onDisable?.();
    } catch {}
    showToast("Lane Connector disabled.", Type.NOTIFICATION.INFO, { duration: 2000 });
    cleanup();
    return;
  }

  if (st.phase === "waitCommit") {
    if (e.key === "v" || e.key === "V") {
      e.preventDefault();
      commitConnector("v");
    } else if (e.key === "l" || e.key === "L") {
      e.preventDefault();
      commitConnector("l");
    }
  }
};

const cleanup = () => {
  if (!st) st = makeInitialState();

  try {
    if (st.handler) st.handler.destroy();
  } catch {}
  st.handler = null;

  clearHighlights();
  destroyOverlay();
  unlockCamera();

  try {
    window.removeEventListener("keydown", onKeyDown);
  } catch {}

  st = makeInitialState();
};

const setupHandler = () => {
  const viewer = st.viewer;
  if (!viewer) return;

  ensureOverlay();
  st.handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  const beginDrag = (pos) => {
    st.dragging = true;
    st.dragStart = pos;
    st.dragEnd = pos;
    lockCamera();
    drawOverlayRect(clampRect(st.dragStart, st.dragEnd));
  };

  const updateDrag = (pos) => {
    if (!st.dragging) return;
    st.dragEnd = pos;
    drawOverlayRect(clampRect(st.dragStart, st.dragEnd));
  };

  const endDrag = () => {
    if (!st.dragging || !st.dragStart || !st.dragEnd) return;

    st.dragging = false;
    unlockCamera();

    const rect = clampRect(st.dragStart, st.dragEnd);
    drawOverlayRect(null);

    if (rect.width < 4 || rect.height < 4) return;

    const sources = [st.laneMarkerDs, st.virtualLineDs].filter(Boolean);
    const hits = scanPolylinesInRect(viewer, rect, sources);

    if (hits.length !== 1) {
      showToast(`Invalid selection. Found ${hits.length}. Need exactly 1.`, Type.NOTIFICATION.ERROR, { duration: 3500 });
      return;
    }

    if (st.phase === "pickCandidate") {
      acceptCandidateClick(hits[0]);
      return;
    }

    acceptStartLine(hits[0]);
  };

  st.handler.setInputAction((e) => {
    if (!e?.position) return;

    if (st.phase === "waitCommit") return;

    const ent = pickPolylineByClick(viewer, e.position);
    if (ent?.polyline) {
      if (st.phase === "pickCandidate") acceptCandidateClick(ent);
      else {
        if (!st.autoAdvance && st.nextStartLine?.polyline) {
          // continue from last candidate created
          acceptStartLine(st.nextStartLine);
          st.nextStartLine = null;
        } else {
          acceptStartLine(ent);
        }
      }
      return;
    }

    // beginDrag(e.position);
    return;
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  // st.handler.setInputAction((e) => {
  //   if (!e?.endPosition) return;
  //   updateDrag(e.endPosition);
  // }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  // st.handler.setInputAction(() => {
  //   endDrag();
  // }, Cesium.ScreenSpaceEventType.LEFT_UP);
};

// -----------------------
// Public API
// -----------------------

export function setLaneConnectorAutoAdvance(v) {
  st.autoAdvance = v !== false; // default true
}

export function toggleLaneConnectorTool(viewer, disable, laneMarkerDs, virtualLineDs, originalCameraControls, onDisable, settings, markEditedCb, userData, mapObjectsRef = null, setMapObjects = null) {  if (!viewer) return;

  if (disable) {
    if (!st?.enabled) return;
    showToast("Lane Connector disabled.", Type.NOTIFICATION.INFO, { duration: 2000 });
    cleanup();
    return;
  }

  cleanup();

  st.enabled = true;
  st.viewer = viewer;

  st.laneMarkerDs = laneMarkerDs || null;
  st.virtualLineDs = virtualLineDs || null;

  st.originalCameraControls = originalCameraControls || null;
  st.camLocked = false;

  st.onDisable = typeof onDisable === "function" ? onDisable : null;
   st.markEdited = typeof markEditedCb === "function" ? markEditedCb : null;
  st.userData = userData || null;
  st.mapObjectsRef = mapObjectsRef || null;
  st.setMapObjects = typeof setMapObjects === "function" ? setMapObjects : null;

  st.cfg = { ...DEFAULTS, ...(settings || {}) };

  // angle is an alias for maxGapAngleDeg (used by Settings UI)
  if (Number.isFinite(st.cfg?.angle)) st.cfg.maxGapAngleDeg = st.cfg.angle;

  const hasAny = Boolean(st.laneMarkerDs || st.virtualLineDs);
  if (!hasAny) {
    showToast("Lane Connector needs LaneMarker or VirtualLine dataSource.", Type.NOTIFICATION.ERROR, { duration: 4500 });
    cleanup();
    return;
  }

  rebuildIndexAndCaches();

  setupHandler();
  window.addEventListener("keydown", onKeyDown);

  showToast("Select start line (click). Only LaneMarker/VirtualLine. ESC to exit.", Type.NOTIFICATION.SUCCESS, { duration: 5500 });
}
